/* =====================================================================
 * useSlashMenu — 슬래시 메뉴 상태 · 로직 · 아이콘
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import type { Node } from '@tiptap/pm/model';
import { NodeSelection, Selection } from '@tiptap/pm/state';
import { scheduleForceFixTables } from './tableFix';

/* ------------------------------------------------------------------ */
/* 메뉴 아이콘                                                          */
/* ------------------------------------------------------------------ */

const ICON_TABLE = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 },
  React.createElement('rect', { x: 1.5, y: 1.5, width: 13, height: 13, rx: 1.5 }),
  React.createElement('line', { x1: 1.5, y1: 5.5, x2: 14.5, y2: 5.5 }),
  React.createElement('line', { x1: 1.5, y1: 10, x2: 14.5, y2: 10 }),
  React.createElement('line', { x1: 5.5, y1: 1.5, x2: 5.5, y2: 14.5 }),
  React.createElement('line', { x1: 10.5, y1: 1.5, x2: 10.5, y2: 14.5 }),
);

const ICON_UL = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.3 },
  React.createElement('line', { x1: 5, y1: 4, x2: 14, y2: 4 }),
  React.createElement('line', { x1: 5, y1: 8, x2: 14, y2: 8 }),
  React.createElement('line', { x1: 5, y1: 12, x2: 14, y2: 12 }),
  React.createElement('circle', { cx: 2.5, cy: 4, r: 1.3 }),
  React.createElement('circle', { cx: 2.5, cy: 8, r: 1.3 }),
  React.createElement('circle', { cx: 2.5, cy: 12, r: 1.3 }),
);

const ICON_OL = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.3 },
  React.createElement('line', { x1: 5, y1: 4, x2: 14, y2: 4 }),
  React.createElement('line', { x1: 5, y1: 8, x2: 14, y2: 8 }),
  React.createElement('line', { x1: 5, y1: 12, x2: 14, y2: 12 }),
  React.createElement('text', { x: 1, y: 5.5, fontSize: 5, fill: 'currentColor', stroke: 'none', fontFamily: 'sans-serif' }, '1.'),
  React.createElement('text', { x: 1, y: 9.5, fontSize: 5, fill: 'currentColor', stroke: 'none', fontFamily: 'sans-serif' }, '2.'),
  React.createElement('text', { x: 1, y: 13.5, fontSize: 5, fill: 'currentColor', stroke: 'none', fontFamily: 'sans-serif' }, '3.'),
);

/* 할 일 목록 아이콘 */
const ICON_TASK = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.3 },
  React.createElement('rect', { x: 1.5, y: 2, width: 5, height: 5, rx: 1 }),
  React.createElement('polyline', { points: '2.5 4.5 3.5 5.5 5.5 3' }),
  React.createElement('line', { x1: 8.5, y1: 4.5, x2: 14.5, y2: 4.5 }),
  React.createElement('rect', { x: 1.5, y: 9, width: 5, height: 5, rx: 1 }),
  React.createElement('line', { x1: 8.5, y1: 11.5, x2: 14.5, y2: 11.5 }),
);

/* 토글 목록 아이콘 */
const ICON_TOGGLE = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'currentColor', stroke: 'none' },
  React.createElement('polygon', { points: '4,3 12,8 4,13' }),
);

/* 인용 아이콘 */
const ICON_QUOTE = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'currentColor', stroke: 'none' },
  React.createElement('path', { d: 'M3.5 4C2.67 4 2 4.67 2 5.5v2C2 8.33 2.67 9 3.5 9H5l-1 3h1.5l1-3h.5V5.5C7 4.67 6.33 4 5.5 4H3.5zM10 4c-.83 0-1.5.67-1.5 1.5v2c0 .83.67 1.5 1.5 1.5h1.5l-1 3H12l1-3h.5V5.5c0-.83-.67-1.5-1.5-1.5H10z' }),
);

/* 구분선 아이콘 */
const ICON_DIVIDER = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round' },
  React.createElement('line', { x1: 2, y1: 8, x2: 14, y2: 8 }),
);

/* ── 미디어 아이콘 ── */

const ICON_IMAGE = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 },
  React.createElement('rect', { x: 1.5, y: 2, width: 13, height: 12, rx: 2 }),
  React.createElement('circle', { cx: 5, cy: 6, r: 1.2 }),
  React.createElement('path', { d: 'M14.5 11l-4-4L3 14', strokeLinejoin: 'round' }),
);

const ICON_VIDEO = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 },
  React.createElement('rect', { x: 1.5, y: 3, width: 13, height: 10, rx: 2 }),
  React.createElement('polygon', { points: '6.5,5.5 11,8 6.5,10.5', fill: 'currentColor', stroke: 'none' }),
);

const ICON_AUDIO = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 },
  React.createElement('path', { d: 'M6 12V4l8-1.5v9' }),
  React.createElement('circle', { cx: 4, cy: 12, r: 2 }),
  React.createElement('circle', { cx: 12, cy: 10.5, r: 2 }),
);

const ICON_FILE = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 },
  React.createElement('path', { d: 'M9 1H4a1.5 1.5 0 00-1.5 1.5v11A1.5 1.5 0 004 15h8a1.5 1.5 0 001.5-1.5V5.5L9 1z' }),
  React.createElement('polyline', { points: '9,1 9,6 13.5,6' }),
);

const ICON_BOOKMARK = React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 },
  React.createElement('path', { d: 'M7 9a3.5 3.5 0 005.3.38l2.1-2.1a3.5 3.5 0 00-4.95-4.95L8.35 3.4' }),
  React.createElement('path', { d: 'M9 7a3.5 3.5 0 00-5.3-.38l-2.1 2.1a3.5 3.5 0 004.95 4.95l1.1-1.07' }),
);

export function renderMenuIcon(icon: string): React.ReactNode {
  switch (icon) {
    case 'table': return ICON_TABLE;
    case 'code': return React.createElement('span', { style: { fontFamily: 'monospace', fontSize: 12, fontWeight: 600 } }, '</>');
    case 'text': return React.createElement('span', { style: { fontFamily: 'serif', fontSize: 14, fontWeight: 600 } }, 'T');
    case 'h1': return React.createElement('span', { style: { fontFamily: 'sans-serif', fontSize: 12, fontWeight: 700 } }, 'H1');
    case 'h2': return React.createElement('span', { style: { fontFamily: 'sans-serif', fontSize: 12, fontWeight: 700 } }, 'H2');
    case 'h3': return React.createElement('span', { style: { fontFamily: 'sans-serif', fontSize: 12, fontWeight: 700 } }, 'H3');
    case 'ul': return ICON_UL;
    case 'ol': return ICON_OL;
    case 'task': return ICON_TASK;
    case 'toggle': return ICON_TOGGLE;
    case 'quote': return ICON_QUOTE;
    case 'divider': return ICON_DIVIDER;
    case 'image': return ICON_IMAGE;
    case 'video': return ICON_VIDEO;
    case 'audio': return ICON_AUDIO;
    case 'file': return ICON_FILE;
    case 'bookmark': return ICON_BOOKMARK;
    default: return null;
  }
}

type SlashItemCategory = 'basic' | 'media' | 'table';

interface SlashMenuItem {
  id: string;
  label: string;
  keywords: string[];
  icon: string;
  shortcut?: string;
  category: SlashItemCategory;
}

interface SlashMenuSection {
  section: string;
  items: SlashMenuItem[];
}

interface SlashUsageStat {
  count: number;
  lastUsedAt: number;
}

interface SlashContext {
  parentType: string;
  isEmptyParagraph: boolean;
  inTable: boolean;
  inList: boolean;
  previousBlockType: string | null;
}

const SLASH_USAGE_KEY = 'document_slash_usage_v1';
const MEDIA_ITEM_IDS = new Set(['image', 'video', 'audio', 'file', 'bookmark']);
const TABLE_ITEM_IDS = new Set(['table', 'table-view']);
const LIST_ITEM_IDS = new Set(['bullet-list', 'numbered-list', 'task-list']);

const BASE_MENU_SECTIONS: SlashMenuSection[] = [
  {
    section: '기본 블록',
    items: [
      { id: 'text', label: '텍스트', keywords: ['텍스트', 'text', '본문'], icon: 'text', category: 'basic' },
      { id: 'h1', label: '제목1', keywords: ['제목', 'heading', 'h1'], icon: 'h1', shortcut: '#', category: 'basic' },
      { id: 'h2', label: '제목2', keywords: ['제목', 'heading', 'h2'], icon: 'h2', shortcut: '##', category: 'basic' },
      { id: 'h3', label: '제목3', keywords: ['제목', 'heading', 'h3'], icon: 'h3', shortcut: '###', category: 'basic' },
      { id: 'bullet-list', label: '글머리 기호 목록', keywords: ['글머리', 'bullet', 'list', '목록'], icon: 'ul', shortcut: '-', category: 'basic' },
      { id: 'numbered-list', label: '번호 매기기 목록', keywords: ['번호', 'numbered', 'list', '목록'], icon: 'ol', shortcut: '1.', category: 'basic' },
      { id: 'task-list', label: '할 일 목록', keywords: ['할 일', 'todo', 'task', 'checkbox', '체크'], icon: 'task', shortcut: '[]', category: 'basic' },
      { id: 'toggle', label: '토글 목록', keywords: ['토글', 'toggle', '접기', '펼치기', 'details'], icon: 'toggle', category: 'basic' },
      { id: 'quote', label: '인용', keywords: ['인용', 'quote', 'blockquote', '따옴표'], icon: 'quote', shortcut: '>', category: 'basic' },
      { id: 'divider', label: '구분선', keywords: ['구분선', 'divider', 'hr', 'horizontal', '선'], icon: 'divider', shortcut: '---', category: 'basic' },
      { id: 'table', label: '표', keywords: ['표', 'table', '테이블'], icon: 'table', category: 'table' },
      { id: 'table-view', label: '표 보기 · 데이터베이스', keywords: ['표', 'database', '데이터베이스'], icon: 'table', category: 'table' },
    ],
  },
  {
    section: '미디어',
    items: [
      { id: 'image', label: '이미지', keywords: ['이미지', 'image', '사진', '그림'], icon: 'image', category: 'media' },
      { id: 'video', label: '동영상', keywords: ['동영상', 'video', '비디오', '영상'], icon: 'video', category: 'media' },
      { id: 'audio', label: '오디오', keywords: ['오디오', 'audio', '음악', '음성', '녹음'], icon: 'audio', category: 'media' },
      { id: 'file', label: '파일', keywords: ['파일', 'file', '첨부', '문서'], icon: 'file', category: 'media' },
      { id: 'code', label: '코드', keywords: ['코드', 'code'], icon: 'code', shortcut: '···', category: 'media' },
      { id: 'bookmark', label: '북마크', keywords: ['북마크', 'bookmark', '링크', 'url', 'link'], icon: 'bookmark', category: 'media' },
    ],
  },
];

function loadSlashUsage(): Record<string, SlashUsageStat> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SLASH_USAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, SlashUsageStat>;
  } catch {
    return {};
  }
}

function saveSlashUsage(stats: Record<string, SlashUsageStat>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SLASH_USAGE_KEY, JSON.stringify(stats));
  } catch {
    // 저장 실패 시 무시
  }
}

function isSubsequence(query: string, target: string): boolean {
  let qi = 0;
  for (let i = 0; i < target.length && qi < query.length; i += 1) {
    if (target[i] === query[qi]) qi += 1;
  }
  return qi === query.length;
}

function getQueryScore(item: SlashMenuItem, query: string): number {
  if (!query) return 8;
  const q = query.toLowerCase();
  const label = item.label.toLowerCase();
  const keywords = item.keywords.map((k) => k.toLowerCase());
  const haystack = `${label} ${keywords.join(' ')}`;

  if (label === q) return 120;
  if (label.startsWith(q)) return 96;
  if (keywords.some((k) => k === q)) return 88;
  if (keywords.some((k) => k.startsWith(q))) return 80;
  if (label.includes(q)) return 68;
  if (haystack.includes(q)) return 58;
  if (isSubsequence(q, haystack)) return 36;
  return -999;
}

function getSlashContext(editor: Editor | null): SlashContext {
  if (!editor) {
    return {
      parentType: 'paragraph',
      isEmptyParagraph: true,
      inTable: false,
      inList: false,
      previousBlockType: null,
    };
  }

  const { $from } = editor.state.selection;
  const parentType = $from.parent.type.name;
  const isEmptyParagraph = parentType === 'paragraph' && $from.parent.textContent.length === 0;

  let inTable = false;
  let inList = false;
  for (let d = $from.depth; d >= 1; d -= 1) {
    const typeName = $from.node(d).type.name;
    if (typeName === 'table' || typeName === 'tableRow' || typeName === 'tableCell' || typeName === 'tableHeader') {
      inTable = true;
    }
    if (typeName === 'bulletList' || typeName === 'orderedList' || typeName === 'taskList') {
      inList = true;
    }
  }

  let previousBlockType: string | null = null;
  try {
    const topBlockPos = $from.depth >= 1 ? $from.before(1) : 0;
    const before = editor.state.doc.childBefore(topBlockPos);
    previousBlockType = before.node?.type.name ?? null;
  } catch {
    previousBlockType = null;
  }

  return {
    parentType,
    isEmptyParagraph,
    inTable,
    inList,
    previousBlockType,
  };
}

function getContextScore(item: SlashMenuItem, ctx: SlashContext): number {
  let score = 0;

  if (ctx.isEmptyParagraph) {
    if (MEDIA_ITEM_IDS.has(item.id) || TABLE_ITEM_IDS.has(item.id) || item.id === 'code') score += 16;
    if (item.id === 'divider') score -= 8;
  } else {
    if (item.id === 'text' || LIST_ITEM_IDS.has(item.id)) score += 6;
  }

  if (ctx.inTable) {
    if (MEDIA_ITEM_IDS.has(item.id) || TABLE_ITEM_IDS.has(item.id)) score -= 24;
    if (item.id === 'text' || LIST_ITEM_IDS.has(item.id) || item.id === 'code') score += 8;
  }

  if (ctx.inList) {
    if (LIST_ITEM_IDS.has(item.id) || item.id === 'text') score += 8;
    if (TABLE_ITEM_IDS.has(item.id)) score -= 12;
  }

  if (ctx.previousBlockType === 'imageBlock' || ctx.previousBlockType === 'videoBlock') {
    if (item.id === 'text') score += 10;
    if (item.id === 'divider') score += 4;
  }

  if (ctx.parentType === 'codeBlock' && item.id !== 'text') {
    score -= 8;
  }

  return score;
}

function getPersonalScore(itemId: string, usage: Record<string, SlashUsageStat>): number {
  const s = usage[itemId];
  if (!s) return 0;
  const days = (Date.now() - s.lastUsedAt) / (1000 * 60 * 60 * 24);
  const recency = Math.max(0, 18 - days * 2.2);
  const frequency = Math.min(18, Math.log2(s.count + 1) * 6.2);
  return recency + frequency;
}

/* ------------------------------------------------------------------ */
/* 훅 본체                                                              */
/* ------------------------------------------------------------------ */

interface UseSlashMenuOptions {
  editor: Editor | null;
  /** DragHandle에서 추적 중인 블록 정보 */
  nodeInfoRef: React.MutableRefObject<{ node: Node | null; pos: number }>;
  /** EditorBody ref — 위치 계산용 */
  editorBodyRef: React.RefObject<HTMLDivElement | null>;
  /** P11-5g: when false, the "미디어" section (image/video/audio/file/bookmark)
   * is hidden from the slash menu so the editor cannot insert these blocks. */
  mediaEmbed?: boolean;
}

export function useSlashMenu({ editor, nodeInfoRef, editorBodyRef, mediaEmbed = true }: UseSlashMenuOptions) {
  const TABLE_DEBUG = import.meta.env.DEV;
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashMenuPos, setSlashMenuPos] = useState<{ topDown: number; topUp: number; left: number }>({ topDown: 0, topUp: 0, left: 0 });
  const [slashMenuDirection, setSlashMenuDirection] = useState<'down' | 'up'>('down');
  const [slashQuery, setSlashQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const slashMenuRef = useRef<HTMLDivElement>(null);
  const slashPosRef = useRef<number | null>(null);
  const selectedIdxRef = useRef(0);

  const [usageStats, setUsageStats] = useState<Record<string, SlashUsageStat>>(() =>
    loadSlashUsage(),
  );

  useEffect(() => {
    saveSlashUsage(usageStats);
  }, [usageStats]);

  /* P11-5g: when `mode.doc-agent.mediaEmbed` is off the corresponding
   * slash-menu items are stripped (the underlying TipTap nodes are
   * also unregistered in extensions.ts so insertion would no-op). */
  const sections = useMemo<SlashMenuSection[]>(
    () => BASE_MENU_SECTIONS.map((s) => ({
      ...s,
      items: mediaEmbed
        ? s.items
        : s.items.filter((item) => !MEDIA_ITEM_IDS.has(item.id)),
    })).filter((s) => s.items.length > 0),
    [mediaEmbed],
  );

  const allItems = useMemo(
    () => sections.flatMap((s) => s.items),
    [sections],
  );

  const slashContext = useMemo(
    () => getSlashContext(editor),
    [editor, slashQuery, slashMenuOpen, slashMenuPos.left, slashMenuPos.topDown],
  );

  const recordUsage = useCallback((itemId: string) => {
    setUsageStats((prev) => ({
      ...prev,
      [itemId]: {
        count: (prev[itemId]?.count ?? 0) + 1,
        lastUsedAt: Date.now(),
      },
    }));
  }, []);

  /* 필터링 + 추천 랭킹 */
  const filteredSections = useMemo(() => {
    const q = slashQuery.trim().toLowerCase();

    const scored = allItems
      .map((item) => ({
        item,
        queryScore: getQueryScore(item, q),
      }))
      .filter((x) => x.queryScore > -999)
      .map((x) => ({
        item: x.item,
        score:
          x.queryScore * 0.6 +
          getContextScore(x.item, slashContext) * 0.25 +
          getPersonalScore(x.item.id, usageStats) * 0.15,
      }))
      .sort((a, b) => b.score - a.score);

    const recommended: SlashMenuItem[] = [];
    const categoryCount: Record<SlashItemCategory, number> = {
      basic: 0,
      media: 0,
      table: 0,
    };
    for (const row of scored) {
      if (recommended.some((x) => x.id === row.item.id)) continue;
      if (categoryCount[row.item.category] >= 2 && recommended.length < 4) continue;
      recommended.push(row.item);
      categoryCount[row.item.category] += 1;
      if (recommended.length >= 5) break;
    }

    const recommendedIds = new Set(recommended.map((x) => x.id));
    const baseSections = sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          const queryScore = getQueryScore(item, q);
          if (queryScore <= -999) return false;
          return !recommendedIds.has(item.id);
        }),
      }))
      .filter((section) => section.items.length > 0);

    const out: SlashMenuSection[] = [];
    if (recommended.length > 0) {
      out.push({ section: '추천', items: recommended });
    }
    out.push(...baseSections);
    return out;
  }, [slashQuery, allItems, slashContext, usageStats, sections]);

  /* 섹션을 펼친 1차원 아이템 목록 (키보드 네비게이션용) */
  const flatItems = useMemo(
    () => filteredSections.flatMap(s => s.items),
    [filteredSections],
  );

  /* 필터 변경 시 선택 인덱스 초기화 */
  useEffect(() => {
    setSelectedIndex(0);
    selectedIdxRef.current = 0;
  }, [slashQuery]);

  /* 키보드 핸들러에서 참조할 최신 값 refs */
  const flatItemsRef = useRef(flatItems);
  flatItemsRef.current = flatItems;
  const executeRef = useRef<(id: string) => void>(() => {});

  /* 메뉴 항목 실행 */
  const insertBlockWithFallback = useCallback(
    (type: string, attrs?: Record<string, unknown>) => {
      if (!editor) return;

      const inserted = editor.chain().focus().insertContent({ type, attrs }).run();
      if (inserted) return;

      const nodeType = editor.state.schema.nodes[type];
      if (!nodeType) return;

      try {
        // 특정 컨텍스트(코드블록/테이블 셀 등)에서 insertContent가 실패하면 문서 끝에라도 보장 삽입
        const end = editor.state.doc.content.size;
        const tr = editor.state.tr.insert(end, nodeType.create(attrs ?? {}));
        tr.setSelection(Selection.near(tr.doc.resolve(Math.min(end + 1, tr.doc.content.size))));
        editor.view.dispatch(tr.scrollIntoView());
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useSlashMenu] block insert fallback failed:', err);
      }
    },
    [editor],
  );

  const executeSlashCommand = useCallback((itemId: string) => {
    if (!editor) return;

    // "/" 와 뒤의 쿼리 텍스트 삭제
    if (slashPosRef.current !== null) {
      const from = slashPosRef.current;
      const to = editor.state.selection.to;
      editor.chain().deleteRange({ from, to }).focus().run();
    }

    recordUsage(itemId);

    switch (itemId) {
      case 'text':
        editor.chain().focus().setParagraph().run();
        break;
      case 'h1':
        editor.chain().focus().setHeading({ level: 1 }).run();
        break;
      case 'h2':
        editor.chain().focus().setHeading({ level: 2 }).run();
        break;
      case 'h3':
        editor.chain().focus().setHeading({ level: 3 }).run();
        break;
      case 'bullet-list':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'numbered-list':
        editor.chain().focus().toggleOrderedList().run();
        break;
      case 'code':
        editor.chain().focus().toggleCodeBlock().run();
        break;
      case 'task-list':
        editor.chain().focus().toggleTaskList().run();
        break;
      case 'toggle':
        editor.chain().focus().setDetails().run();
        break;
      case 'quote':
        editor.chain().focus().setBlockquote().run();
        break;
      case 'divider':
        editor.chain().focus().setHorizontalRule().run();
        break;
      case 'table':
      case 'table-view': {
        if (TABLE_DEBUG) {
          const w = window as Window & { __TABLE_DEBUG_LOGS__?: Array<Record<string, unknown>> };
          const entry = {
            at: new Date().toISOString(),
            label: 'insert-table:useSlashMenu',
            payload: {
              itemId,
              selectionFrom: editor.state.selection.from,
              selectionTo: editor.state.selection.to,
            },
          };
          if (!w.__TABLE_DEBUG_LOGS__) w.__TABLE_DEBUG_LOGS__ = [];
          w.__TABLE_DEBUG_LOGS__.push(entry);
          // eslint-disable-next-line no-console
          console.log('[TableDebug] insert-table:useSlashMenu', entry.payload);
        }
        // 빈 paragraph를 NodeSelection으로 잡아서 insertTable이 해당 블록을 교체하도록 함
        const { $from: $tblF } = editor.state.selection;
        if ($tblF.parent.type.name === 'paragraph' && $tblF.parent.textContent === '') {
          try {
            const bPos = $tblF.before($tblF.depth);
            editor.view.dispatch(
              editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, bPos)),
            );
          } catch { /* NodeSelection 실패 시 기본 동작 유지 */ }
        }
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
        scheduleForceFixTables(editor);
        break;
      }
      case 'image':
        insertBlockWithFallback('imageBlock');
        break;
      case 'video':
        insertBlockWithFallback('videoBlock');
        break;
      case 'audio':
        insertBlockWithFallback('audioBlock');
        break;
      case 'file':
        insertBlockWithFallback('fileBlock');
        break;
      case 'bookmark':
        insertBlockWithFallback('bookmarkBlock');
        break;
      default:
        break;
    }

    setSlashMenuOpen(false);
    setSlashQuery('');
    slashPosRef.current = null;
  }, [editor, TABLE_DEBUG, recordUsage, insertBlockWithFallback]);

  // executeSlashCommand ref 동기화 (keydown 핸들러에서 최신 함수 참조)
  executeRef.current = executeSlashCommand;

  /* 메뉴 열기 헬퍼 */
  const openSlashMenu = useCallback((coords: { topDown: number; topUp: number; left: number; viewportY: number }) => {
    const viewportH = window.innerHeight;
    setSlashMenuDirection(coords.viewportY > viewportH * 0.6 ? 'up' : 'down');
    setSlashMenuPos({ topDown: coords.topDown, topUp: coords.topUp, left: coords.left });
    setSlashMenuOpen(true);
  }, []);

  /* 메뉴 닫기 헬퍼 */
  const closeSlashMenu = useCallback((deleteSlash = false) => {
    setSlashMenuOpen(false);
    setSlashQuery('');
    if (deleteSlash && editor && slashPosRef.current !== null) {
      const pos = slashPosRef.current;
      const text = editor.state.doc.textBetween(pos, editor.state.selection.to, '', '');
      editor.chain()
        .deleteRange({ from: pos, to: pos + 1 + text.length })
        .focus()
        .run();
    }
    slashPosRef.current = null;
  }, [editor]);

  /* + 버튼 클릭 → 현재 블록 아래에 새 빈 블록 삽입 후 메뉴 열기 */
  const handlePlusBtnClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!editor) return;
      if (slashMenuOpen) { closeSlashMenu(); return; }

      const { node, pos } = nodeInfoRef.current;
      if (!node) return;

      const isEmpty = node.type.name === 'paragraph' && node.textContent.length === 0;

      if (isEmpty) {
        // 빈 블록 → 그 자리에서 바로 메뉴 열기
        editor.commands.focus(pos + 1);

        const domNode = editor.view.nodeDOM(pos) as HTMLElement | null;
        if (!domNode) return;
        const rect = domNode.getBoundingClientRect();
        const bodyRect = editorBodyRef.current?.getBoundingClientRect();
        const scrollTop = editorBodyRef.current?.scrollTop ?? 0;
        const bodyTop = bodyRect?.top ?? 0;
        const bodyLeft = bodyRect?.left ?? 0;

        slashPosRef.current = null;
        setSlashQuery('');
        openSlashMenu({
          topDown: rect.bottom - bodyTop + scrollTop + 6,
          topUp: rect.top - bodyTop + scrollTop - 6,
          left: rect.left - bodyLeft,
          viewportY: rect.bottom,
        });
      } else {
        // 내용이 있는 블록 → 아래에 빈 paragraph 삽입 후 메뉴 열기
        const endOfBlock = pos + node.nodeSize;
        const { tr } = editor.state;
        const newParagraph = editor.state.schema.nodes.paragraph.create();
        tr.insert(endOfBlock, newParagraph);
        tr.setSelection(Selection.near(tr.doc.resolve(endOfBlock + 1)));
        editor.view.dispatch(tr);
        editor.view.focus();

        requestAnimationFrame(() => {
          try {
            const coords = editor.view.coordsAtPos(endOfBlock + 1);
            const bodyRect = editorBodyRef.current?.getBoundingClientRect();
            const scrollTop = editorBodyRef.current?.scrollTop ?? 0;
            const bodyTop = bodyRect?.top ?? 0;
            const bodyLeft = bodyRect?.left ?? 0;

            slashPosRef.current = null;
            setSlashQuery('');
            openSlashMenu({
              topDown: coords.bottom - bodyTop + scrollTop + 6,
              topUp: coords.top - bodyTop + scrollTop - 6,
              left: coords.left - bodyLeft,
              viewportY: coords.bottom,
            });
          } catch { /* 방어 */ }
        });
      }
    },
    [editor, slashMenuOpen, closeSlashMenu, openSlashMenu, nodeInfoRef, editorBodyRef],
  );

  /* "/" 입력 감지 */
  useEffect(() => {
    if (!editor) return;

    const handleTransaction = () => {
      const { state } = editor;
      const { selection } = state;
      const { $from } = selection;

      const blockStart = $from.start();
      const cursorPos = $from.pos;
      const textBefore = state.doc.textBetween(blockStart, cursorPos, '', '');

      const slashIdx = textBefore.lastIndexOf('/');
      if (slashIdx !== -1) {
        const query = textBefore.slice(slashIdx + 1);
        const docSlashPos = blockStart + slashIdx;

        if (slashMenuOpen && slashPosRef.current !== null) {
          setSlashQuery(query);
          return;
        }

        slashPosRef.current = docSlashPos;
        setSlashQuery(query);

        try {
          const coords = editor.view.coordsAtPos(cursorPos);
          const bodyRect = editorBodyRef.current?.getBoundingClientRect();
          const scrollTop = editorBodyRef.current?.scrollTop ?? 0;
          const bodyTop = bodyRect?.top ?? 0;
          const bodyLeft = bodyRect?.left ?? 0;

          const $pos = editor.state.doc.resolve(cursorPos);
          const blockPos = $pos.before($pos.depth);
          const blockDom = editor.view.nodeDOM(blockPos) as HTMLElement | null;
          const blockRect = blockDom?.getBoundingClientRect();

          openSlashMenu({
            topDown: coords.bottom - bodyTop + scrollTop + 6,
            topUp: coords.top - bodyTop + scrollTop - 6,
            left: blockRect ? blockRect.left - bodyLeft : coords.left - bodyLeft,
            viewportY: coords.bottom,
          });
        } catch { /* 방어 */ }
      } else if (slashMenuOpen && slashPosRef.current !== null) {
        closeSlashMenu();
      }
    };

    editor.on('selectionUpdate', handleTransaction);
    editor.on('update', handleTransaction);
    return () => {
      editor.off('selectionUpdate', handleTransaction);
      editor.off('update', handleTransaction);
    };
  }, [editor, slashMenuOpen, openSlashMenu, closeSlashMenu, editorBodyRef]);

  /* 키보드 네비게이션 + ESC / 외부 클릭으로 메뉴 닫기 */
  useEffect(() => {
    if (!slashMenuOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const items = flatItemsRef.current;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          closeSlashMenu(true);
          break;

        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          if (items.length > 0) {
            setSelectedIndex(i => {
              const next = (i + 1) % items.length;
              selectedIdxRef.current = next;
              return next;
            });
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (items.length > 0) {
            setSelectedIndex(i => {
              const next = (i - 1 + items.length) % items.length;
              selectedIdxRef.current = next;
              return next;
            });
          }
          break;

        case 'Enter': {
          const item = items[selectedIdxRef.current];
          if (item) {
            e.preventDefault();
            e.stopPropagation();
            executeRef.current(item.id);
          }
          break;
        }

        default:
          break;
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (slashMenuRef.current && !slashMenuRef.current.contains(target)) {
        closeSlashMenu();
      }
    };

    // capture 단계에서 처리하여 ProseMirror보다 먼저 가로챔
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [slashMenuOpen, closeSlashMenu]);

  /* 선택된 아이템 자동 스크롤 */
  useEffect(() => {
    if (!slashMenuOpen) return;
    const container = slashMenuRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLElement>(`[data-slash-index="${selectedIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, slashMenuOpen]);

  /* 미디어 블록 + 버튼에서 발송하는 커스텀 이벤트 수신 → 슬래시 메뉴 열기 */
  useEffect(() => {
    if (!editor) return;

    const handler = (e: Event) => {
      const pos = (e as CustomEvent).detail?.pos as number | undefined;
      if (pos == null) return;

      requestAnimationFrame(() => {
        try {
          const coords = editor.view.coordsAtPos(pos);
          const bodyRect = editorBodyRef.current?.getBoundingClientRect();
          const scrollTop = editorBodyRef.current?.scrollTop ?? 0;
          const bodyTop = bodyRect?.top ?? 0;
          const bodyLeft = bodyRect?.left ?? 0;

          slashPosRef.current = null;
          setSlashQuery('');
          openSlashMenu({
            topDown: coords.bottom - bodyTop + scrollTop + 6,
            topUp: coords.top - bodyTop + scrollTop - 6,
            left: coords.left - bodyLeft,
            viewportY: coords.bottom,
          });
        } catch { /* 방어 */ }
      });
    };

    window.addEventListener('media-block-open-slash', handler);
    return () => window.removeEventListener('media-block-open-slash', handler);
  }, [editor, openSlashMenu, editorBodyRef]);

  return {
    slashMenuOpen,
    slashMenuPos,
    slashMenuDirection,
    slashMenuRef,
    filteredSections,
    flatItems,
    selectedIndex,
    executeSlashCommand,
    closeSlashMenu,
    handlePlusBtnClick,
  };
}
